import {useEffect, useRef, useState} from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";
import {Placement} from "@floating-ui/utils";

type OptionOrString = string | {value: string, label: string}

const optionValue = (option: OptionOrString): string =>
  typeof option === "string" ? option : option.value;

const optionLabel = (option: OptionOrString): string =>
  typeof option === "string" ? option : option.label;

type SelectButtonProps = {
  value: string,
  onChange?: (value: OptionOrString) => Promise<void>,
  options: OptionOrString[],
}

export default function SelectButton({
  value, onChange, options
}: SelectButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(options.findIndex(option => optionValue(option) === value));

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    } else if (selectedIndex !== null) {
      onChange?.(options[selectedIndex]);
    }
    // Fire onChange only when the selection changes; adding onChange/options would re-fire on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const { refs, floatingStyles, context } = useFloating<HTMLElement>({
    placement: "bottom-start" as Placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const listContentRef = useRef<Array<string | null>>(options.map(optionLabel));
  const isTypingRef = useRef(false);

  const click = useClick(context, { event: "mousedown" });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    // This is a large list, allow looping.
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: isOpen ? setActiveIndex : setSelectedIndex,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, role, listNav, typeahead, click]
  );

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setIsOpen(false);
  };

  const selectedItemLabel =
    selectedIndex !== null && options[selectedIndex] !== undefined
      ? optionLabel(options[selectedIndex])
      : undefined;

  return (
    <>
      <div
        tabIndex={0}
        ref={refs.setReference}
        className="border h-8 w-32 px-2 flex flex-row items-center justify-between rounded-lg text-sm"
        {...getReferenceProps()}
      >
        {selectedItemLabel || "Select..."}
        <span className="size-5 shrink-0 icon-[tabler--chevron-down]"/>
      </div>
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className="shadow-xl bg-white dark:!bg-gray-800 overflow-y-auto border-solid border py-2 rounded-lg z-20 focus:outline-none"
              style={{
                ...floatingStyles,
              }}
              {...getFloatingProps()}
            >
              {options.map((option, i) => (
                <div
                  key={optionValue(option)}
                  ref={(node) => {
                    listRef.current[i] = node;
                  }}
                  role="option"
                  tabIndex={i === activeIndex ? 0 : -1}
                  aria-selected={i === selectedIndex && i === activeIndex}
                  className={`p-2 h-8 flex flex-row items-center text-sm cursor-pointer${i === activeIndex ? " bg-neutral-100 dark:bg-gray-700 dark:focus:bg-gray-600 focus:outline-none" : ""}`}
                  {...getItemProps({
                    // Handle pointer select.
                    onClick() {
                      handleSelect(i);
                    },
                    // Handle keyboard select.
                    onKeyDown(event) {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSelect(i);
                      }

                      if (event.key === " " && !isTypingRef.current) {
                        event.preventDefault();
                        handleSelect(i);
                      }
                    },
                  })}
                >
                  {optionLabel(option)}
                </div>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
